import type { Activity, TaskComment } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

export function useTaskComments(taskId: string) {
  return useQuery({
    queryKey: queryKeys.taskComments(taskId),
    queryFn: async (): Promise<TaskComment[]> => {
      const { data, error } = await supabase()
        .from('task_comments')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at');
      if (error) throw error;
      return data;
    },
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, body }: { taskId: string; body: string }) => {
      const { error } = await supabase().from('task_comments').insert({ task_id: taskId, body });
      if (error) throw error;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.comments });
      void queryClient.invalidateQueries({ queryKey: queryKeys.activity });
    },
  });
}

/** A task's history, newest first. */
export function useTaskActivity(taskId: string) {
  return useQuery({
    queryKey: queryKeys.taskActivity(taskId),
    queryFn: async (): Promise<Activity[]> => {
      const { data, error } = await supabase()
        .from('activity')
        .select('*')
        .eq('item_type', 'task')
        .eq('item_id', taskId)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });
}
